import throttle, { debounce as lodashDebounce } from "lodash.throttle";

import { GlobalContext } from "../GlobalContext";
import { Deferred, PromiseUtils } from "../Promises/PromiseUtils";

/**
 * Synchronní callback funkce
 * @includeToDoc
 */
export interface ISyncCallbackFunction {
    (...args: any[]): void;
}

/**
 * Asynchronní callback funkce
 * @includeToDoc
 */
export interface IAsyncCallbackFunction {
    (...args: any[]): Promise<void>;
}

/**
 * Asynchronní callback funkce, s možností pozdějíšho zrušení volání
 * @includeToDoc
 */
export interface IAsyncCancelableCallbackFunction {
    callback: (...args: any[]) => Promise<void>;
    cancel: () => void;
}

/**
 * Pomocné metody pro práci s funkcemi
 * @includeToDoc
 */
export class FunctionUtils {
    /**
     * Zabalí funkci do debounced volání. Limituje tak počet volání stejné funkce v určitém časovém úseku na jediné.
     * Pokud byla funkce volána poprvé spustí se časovač pro její provedení. Pokud je funkce zavolána znovu v během tohoto limitu je časovač vynulován a funkce se odloží o stejnou dobu. Teprve pokud v časovém intervalu nepřijde další nové volání, je funkce provedena.
     * @param callback Funkce, která má být zavolána
     * @param wait Časový limit v ms.
     * @deprecated použít debounce z lodash.throttle
     */
    public static debouncedFunction(callback: ISyncCallbackFunction, wait: number = 50): ISyncCallbackFunction {
        const wrapper = new DebouncedFunction(callback, wait);
        return (args) => wrapper.callback(args);
    }

    /**
     * Zabalí asynchronní funkci do debounced volání. Limituje tak počet volání stejné funkce v určitém časovém úseku na jediné. Umožňuje čekat na skutečné doběhnutí asynchronní funkce.
     * Pokud byla funkce volána poprvé spustí se časovač pro její provedení. Pokud je funkce zavolána znovu v během tohoto limitu je časovač vynulován a funkce se odloží o stejnou dobu. Teprve pokud v časovém intervalu nepřijde další nové volání, je funkce provedena.
     * @param callback Funkce, která má být zavolána
     * @param wait Časový limit v ms.
     */
    public static debouncedFunctionAsync(callback: IAsyncCallbackFunction, wait: number = 50): IAsyncCallbackFunction {
        const wrapper = new DebouncedFunctionAsync(callback, wait);
        return (args) => wrapper.callback(args);
    }

    /**
     * Zabalí asynchronní funkci do debounced volání. Limituje tak počet volání stejné funkce v určitém časovém úseku na jediné. Umožňuje čekat na skutečné doběhnutí asynchronní funkce.
     * Pokud byla funkce volána poprvé spustí se časovač pro její provedení. Pokud je funkce zavolána znovu v během tohoto limitu je časovač vynulován a funkce se odloží o stejnou dobu. Teprve pokud v časovém intervalu nepřijde další nové volání, je funkce provedena.
     * Umožňuje zrušit volání callbacku pomocí vrácené funkce cancel()
     * @param callback Funkce, která má být zavolána
     * @param wait Časový limit v ms.
     * @returns volaný callback + funkci pro zrušení jeho volání
     */
    public static debouncedCancelableFunctionAsync(callback: IAsyncCallbackFunction, wait: number = 50): IAsyncCancelableCallbackFunction {
        const wrapper = new DebouncedFunctionAsync(callback, wait);
        return { callback: (args) => wrapper.callback(args), cancel: () => wrapper.cancel() };
    }

    /**
     * Limituje volání funkce max jednou za zadaný interval
     * @param wait Interval, v kterém funkce bude volána pouze jednou
     * @param immediate Příznak zda funkci provést na začátku intervalu nebo na konci
    */
    public static limitFunctionPerCall(callback: ISyncCallbackFunction, wait: number = 50, immediate: boolean = true): ISyncCallbackFunction {
        const wrapper = new LimitedCallFunction(callback, wait, immediate);
        return (args) => wrapper.callback(args);
    }

    /**
     * Limituje volání asynchronní funkce max jednou za zadaný interval
     * @param wait Interval, v kterém funkce bude volána pouze jednou
     * @param immediate Příznak zda funkci provést na začátku intervalu nebo na konci
     */
    public static limitFunctionPerCallAsync(callback: IAsyncCallbackFunction, wait: number = 50, immediate: boolean = true): IAsyncCallbackFunction {
        const wrapper = new LimitedCallFunctionAsync(callback, wait, immediate);
        return (args) => wrapper.callback(args);
    }
    /**
     * Limituje volání funkce maximálně jednou za daný interval.
     * @param fn Volaná funkce.
     * @param wait Interval v ms, ve kterém bude funkce volána pouze jednou.
     * @param options Umožňuje nastavit chování throttle. Nastavuje kdy se má funkce spustit - leading and/or trealing edge of timeout.
     */
    public static throttleFunctionBounded(fn: any, wait: number): { (): any; cancel: () => void; flush: () => any } {
        return throttle(fn, wait, { "leading": true, "trailing": true });
    }
}
/**
 * Abstraktní třída pro obalení funkce
 */
abstract class FunctionWrapper {
    protected _timeoutToken: number | undefined | null;
    protected readonly _delay: number;

    constructor(waitDelay: number = 50) {
        this._delay = waitDelay;
    }

    public abstract callback(...args: any[]): void;
}

/**
 * Obal pro synchronní funkci
 */
abstract class SyncFunctionWrapper extends FunctionWrapper {
    protected readonly _callback: ISyncCallbackFunction;

    constructor(callback: ISyncCallbackFunction, waitDelay: number = 50) {
        super(waitDelay);
        this._callback = callback;
    }
}

/**
 * Obal pro asynchronní funkci
 */
abstract class AsyncFunctionWrapper extends FunctionWrapper {
    protected readonly _callback: IAsyncCallbackFunction;
    protected _deferred: Deferred<void> | undefined = undefined;

    constructor(callback: IAsyncCallbackFunction, waitDelay: number = 50) {
        super(waitDelay);
        this._callback = callback;
    }
}

class DebouncedFunction extends SyncFunctionWrapper {
    public override callback(...args: any[]): void {
        if (this._timeoutToken)
            this._timeoutToken = null;

        this._timeoutToken = self.setTimeout(this._callback, this._delay, ...args);
    }
}

class DebouncedFunctionAsync extends AsyncFunctionWrapper {
    public override callback(...args: any[]): Promise<void> {
        if (this._deferred === undefined)
            this._deferred = PromiseUtils.defer<void>();

        if (this._timeoutToken)
            this._timeoutToken = null;

        this._timeoutToken = self.setTimeout(() => {
            this._callback(...args)
                .then(() => {
                    if (this._deferred) this._deferred.resolve();
                    this._deferred = undefined;
                }).catch(e => {
                    if (this._deferred) this._deferred.reject(e);
                    this._deferred = undefined;
                });
        }, this._delay);

        return this._deferred.promise;
    }

    /**
     * SLouží pro zrušení volání callbacku
     */
    public cancel(): void {
        if (this._timeoutToken) {
            self.clearTimeout(this._timeoutToken);
            this._timeoutToken = null;
            if (this._deferred) {
                this._deferred.resolve();
            }
            this._deferred = undefined;
        }
    }
}

class LimitedCallFunction extends SyncFunctionWrapper {
    protected readonly _immediate: boolean;

    constructor(callback: ISyncCallbackFunction, waitDelay: number = 50, immediate: boolean = true) {
        super(callback, waitDelay);
        this._immediate = immediate;
    }

    public override callback(...args: any[]): void {
        if (this._timeoutToken) //běží ochranný timer během kterého nelze opakovat volání metody
            return;

        this._timeoutToken = self.setTimeout(() => {
            this._timeoutToken = null;
            if (!this._immediate) //zavolá metodu na konci
                this._callback(...args);
        }, this._delay);
        if (this._immediate) //zavolá metodu na začátku
            this._callback(...args);
    }
}

class LimitedCallFunctionAsync extends AsyncFunctionWrapper {
    protected readonly _immediate: boolean;

    constructor(callback: IAsyncCallbackFunction, waitDelay: number = 50, immediate: boolean = true) {
        super(callback, waitDelay);
        this._immediate = immediate;
    }

    public override callback(...args: any[]): Promise<void> {
        if (this._deferred === undefined)
            this._deferred = PromiseUtils.defer<void>();

        if (!this._timeoutToken) { //běží ochranný timer během kterého nelze opakovat volání metody
            const call = () => {
                this._callback(...args)
                    .then(() => {
                        if (this._deferred) this._deferred.resolve();
                        this._deferred = undefined;
                    }).catch(e => {
                        if (this._deferred) this._deferred.reject(e);
                        this._deferred = undefined;
                    });
            };

            this._timeoutToken = self.setTimeout(() => {
                this._timeoutToken = null;
                if (!this._immediate) { //zavolá metodu na konci
                    call();
                }
            }, this._delay);
            if (this._immediate) {
                call();
            }
        }
        return this._deferred.promise;
    }
}

//#region decorators

/**
 * Dekorátor limituje volání funkce max jednou za zadaný interval
 * @param wait Interval, v kterém funkce bude volána pouze jednou
 * @param immediate Příznak zda funkci provést na začátku intervalu nebo na konci
 */
export function limitCallPerInterval(wait: number, immediate: boolean = false) {
    return (_target: any, _propertyKey: string | symbol, descriptor: PropertyDescriptor) => {
        //originální funkce se nebude volat
        if (wait <= 0) {
            descriptor.value = () => {
                // do nothing
            };
            return descriptor;
        }

        let timeout: number | null;
        const originalMethod = descriptor.value;
        descriptor.value = function(...args: any) {
            if (timeout) //běží ochranný timer během kterého nelze opakovat volání metody
                return;
            const later = (...args: any) => {
                if (timeout)
                    clearTimeout(timeout);
                timeout = null;
                if (!immediate) //zavolá metodu na konci
                    originalMethod.apply(this, args);
            };
            timeout = self.setTimeout(later, wait);
            if (immediate) //zavolá metodu na začátku
                originalMethod.apply(this, args);
        };
        return descriptor;
    };
}

/**
 * Dekorátor, který udělá funkci debounced. Pokud je taková funkce opakovaně volána v zadaném intervalu, její opakované volání se vždy odloží o zadaný interval.
 * @param wait Interval, o který bude další volání funkce odloženo.
 * @param immediate Příznak jestli funkce má být spuštěna na začátku intervalu nebo na konci.
 */
export function debounce(wait: number, immediate: boolean = false) {
    return (_target: any, _propertyKey: string | symbol, descriptor: PropertyDescriptor) => {
        descriptor.value = lodashDebounce(descriptor.value, wait, { leading: immediate, trailing: !immediate });
        return descriptor;
    };
}
//#endregion

GlobalContext.registerType(FunctionUtils);
